import { useState, useEffect, useRef } from 'react'
import ReilmLogo from '../components/ReilmLogo'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from './auth/AuthContext'

const Navbar = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { isAuthenticated, logout } = useAuth()

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }
  const [menuOpen, setMenuOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const headerRef = useRef(null)

  useEffect(() => {
    setMenuOpen(false)
  }, [location.pathname])

  // Review #3, M3.1: the burger toggled itself and nothing else closed the menu, so a press
  // anywhere on the page left it hanging over the content. Both listeners are bound only while it
  // is open, and `pointerdown` rather than `click` so the menu is gone before the press lands on
  // whatever is underneath. The toggle button is inside the header, so the containment check keeps
  // a second press on the burger working as a close rather than closing and immediately reopening.
  useEffect(() => {
    if (!menuOpen) return
    const onPointerDown = (e) => {
      if (!headerRef.current?.contains(e.target)) setMenuOpen(false)
    }
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setMenuOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [menuOpen])

  // D1.1.2: the navbar used to flip state on a single threshold (scrollY > 20). Because
  // it is sticky, changing its own height moves the document under it, which can push the
  // scroll position back across that same threshold — so it oscillated instead of
  // settling. A hysteresis band fixes it: shrink past 48, expand only back under 12, and
  // nothing in between can flip the state.
  useEffect(() => {
    const SHRINK_AT = 48
    const EXPAND_AT = 12
    const onScroll = () =>
      setScrolled((was) => (was ? window.scrollY > EXPAND_AT : window.scrollY > SHRINK_AT))
    onScroll()
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const toLanding = () => navigate('/')

  // Review #3, M4.2: this used to swap to Pipeline once signed in. That was our reading of set #2's
  // item about "Pricing" showing after sign-up — but that item was about the navbar *inside* the
  // platform, and applying it here took the public site with it. Микола's reason for reverting is
  // the one already noted below: the Dashboard button beside it goes to the platform, so the link
  // has no reason to duplicate it. Always Pricing.
  const navLink = { to: '/pricing', label: 'Pricing' }

  return (
    <header
      ref={headerRef}
      className={`sticky top-0 z-30 bg-brand-pageFill/95 backdrop-blur border-b border-black/5 transition-[height] duration-200 ${
        /* D1.1.1: 90px on desktop at rest. */
        scrolled ? 'h-[56px] md:h-[72px]' : 'h-[72px] md:h-[90px]'
      }`}
    >
      <div className="h-full max-w-canvas mx-auto px-4 md:px-0 flex items-center justify-between gap-3">
        <button
          type="button"
          onClick={toLanding}
          className="flex items-center gap-16 shrink-0 group"
          aria-label="REILM home"
        >
          {/* D3.1: the scale hover was ours; Figma also recolours the logo on hover, so the
              two now run together. Landing was not the page Микола filed D3.1 against — it is
              the same logo and the same spec node, and he reviews this navbar closely. */}
          <ReilmLogo
            className={`h-auto transition-all duration-200 group-hover:scale-[1.04] ${
              scrolled ? 'w-[96px] md:w-[128px]' : 'w-[120px] md:w-[160px]'
            }`}
          />
        </button>

        <nav className="hidden md:flex items-center gap-12 text-[16px] font-semibold">
          <Link
            to={navLink.to}
            className={`transition ${
              location.pathname === navLink.to
                ? 'text-brand-primary underline underline-offset-8'
                : 'text-brand-dark hover:text-brand-primary'
            }`}
          >
            {navLink.label}
          </Link>
        </nav>

        <div className="hidden md:flex items-center gap-3 shrink-0">
          {isAuthenticated ? (
            <>
              <button
                onClick={() => navigate('/pipeline')}
                className={`text-[14px] px-5 rounded-full border border-brand-dark/20 text-brand-dark font-semibold hover:text-[#4CBC18] hover:border-[#4CBC18] transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Dashboard
              </button>
              <button
                onClick={handleLogout}
                className={`text-[14px] px-5 rounded-full bg-brand-primary hover:bg-brand-hover text-white font-semibold whitespace-nowrap transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Logout
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => navigate('/login')}
                className={`text-[14px] px-5 rounded-full border border-brand-dark/20 text-brand-dark font-semibold hover:text-[#4CBC18] hover:border-[#4CBC18] transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Login
              </button>
              <button
                onClick={() => navigate('/signup')}
                className={`text-[14px] px-5 rounded-full bg-brand-primary hover:bg-brand-hover text-white font-semibold whitespace-nowrap transition-all ${scrolled ? 'h-[36px]' : 'h-[40px]'}`}
              >
                Analyze Deals Free
              </button>
            </>
          )}
        </div>

        <button
          type="button"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          className="md:hidden w-[40px] h-[40px] flex items-center justify-center rounded-full hover:bg-black/5"
        >
          {menuOpen ? (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M6 6l12 12M18 6L6 18" stroke="#17270D" strokeWidth="2" strokeLinecap="round" />
            </svg>
          ) : (
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <path d="M4 7h16M4 12h16M4 17h16" stroke="#17270D" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>

      {menuOpen && (
        <div className="md:hidden absolute top-full left-0 right-0 z-40 bg-brand-pageFill border-b border-black/5 shadow-md">
          <div className="flex flex-col gap-3 px-6 py-5">
            <Link
              to={navLink.to}
              className={`text-[16px] font-semibold py-2 ${
                location.pathname === navLink.to ? 'text-brand-primary' : 'text-brand-dark'
              }`}
            >
              {navLink.label}
            </Link>
            {isAuthenticated ? (
              <>
                <button
                  onClick={() => navigate('/pipeline')}
                  className="h-[44px] rounded-full border border-brand-dark/20 text-brand-dark text-[14px] font-semibold"
                >
                  Dashboard
                </button>
                <button
                  onClick={handleLogout}
                  className="h-[44px] rounded-full bg-brand-primary text-white text-[14px] font-semibold"
                >
                  Logout
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={() => navigate('/login')}
                  className="h-[44px] rounded-full border border-brand-dark/20 text-brand-dark text-[14px] font-semibold"
                >
                  Login
                </button>
                <button
                  onClick={() => navigate('/signup')}
                  className="h-[44px] rounded-full bg-brand-primary text-white text-[14px] font-semibold"
                >
                  Analyze Deals Free
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  )
}

export default Navbar
